import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Agent, SuperVisor, Ticket } from "@/types";
import { getFormattedDateTime, getUserName } from "@/utils/helpers";
import {
  ChatboxHeader,
  ChatboxBody,
  ChatList,
  ChatListDataContainer,
  ChatUserNameData,
  ChatUserNameTypography,
  ChatTimeTypography,
  ChatMessageBox,
  ChatAttachmentCol,
  ChatAttachmentCount,
} from "@/pages/Customer/styles/TicketDetails.Conversations.styled";
import { Typography, Avatar } from "@mui/material";
import DOMPurify from "dompurify";

interface Props {
  ticketDetails: Ticket | null;
}

const TicketDetailsConversations: React.FC<Props> = ({ ticketDetails }) => {
  const user: Agent | SuperVisor | null = useAuth();
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const conversationList = ticketDetails?.conversations || [];

  const handleDownload = async (url: string) => {
    try {
      // fetch with CORS so we can turn it into a blob
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;

      // derive a filename from the URL
      const parts = url.split("/");
      a.download = parts[parts.length - 1] || "download";

      // append, click, and clean up
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download error", err);
    }
  };

  return (
    <>
      <ChatboxHeader>
        <Typography component="h2"> {ticketDetails?.subject} </Typography>
        {/* <Chip onClick={() => getTicketDetails()} size="small" label="Refresh" color="primary" variant="outlined" /> */}
      </ChatboxHeader>
      <ChatboxBody>
        {[...conversationList]
          .reverse()
          ?.filter((c) => !!c.message)
          .map((item) => (
            <ChatList
              key={item.id}
              className={item?.senderId === user?.id ? "right" : ""}
            >
              <Avatar
                alt={"User"}
                src="/images/static/user-photo2.png"
                sx={{ width: 38, height: 38 }}
              />
              <ChatListDataContainer
                className={item?.senderId === user?.id ? "right-data" : ""}
              >
                <ChatUserNameData>
                  <ChatUserNameTypography>
                    {" "}
                    {getUserName(item?.sender)}{" "}
                  </ChatUserNameTypography>
                  <ChatTimeTypography>
                    {" "}
                    {getFormattedDateTime(item?.createdAt)}{" "}
                    {item.source && `(Recieved via ${item.source})`}{" "}
                  </ChatTimeTypography>
                </ChatUserNameData>
                <ChatMessageBox>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.message),
                    }}
                  />
                </ChatMessageBox>
                {Number(item?.attachmentUrls?.length) > 0 && (
                  <ChatMessageBox>
                    {(() => {
                      const maxPreview = 3;
                      const isExpanded = expandedIdx === item.id;
                      const urls = item.attachmentUrls!;
                      const previewUrls = isExpanded
                        ? urls
                        : urls.slice(0, maxPreview);
                      const extraCount = urls.length - previewUrls.length;

                      return (
                        <>
                          {previewUrls.map((url, idx) => {
                            const isImage =
                              /\.(jpe?g|png|gif|bmp|webp)(\?.*)?$/i.test(url);
                            return (
                              <ChatAttachmentCol
                                key={idx}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleDownload(url)}
                              >
                                {isImage ? (
                                  <img src={url} alt={`attachment-${idx}`} />
                                ) : (
                                  <img
                                    src="/images/file-icon.svg"
                                    alt="file-icon"
                                    className="icon"
                                  />
                                )}
                              </ChatAttachmentCol>
                            );
                          })}
                          {extraCount > 0 && !isExpanded && (
                            <ChatAttachmentCol
                              onClick={() => setExpandedIdx(item.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <ChatAttachmentCount>
                                +{extraCount}
                              </ChatAttachmentCount>
                            </ChatAttachmentCol>
                          )}
                          {isExpanded && urls.length > 3 && (
                            <ChatAttachmentCol
                              onClick={() => setExpandedIdx(null)}
                              style={{
                                cursor: "pointer",
                                background: "#f0f0f0",
                              }}
                            >
                              <ChatAttachmentCount>
                                −{urls.length - 3}
                              </ChatAttachmentCount>
                            </ChatAttachmentCol>
                          )}
                        </>
                      );
                    })()}
                  </ChatMessageBox>
                )}
              </ChatListDataContainer>
            </ChatList>
          ))}
      </ChatboxBody>
    </>
  );
};

export default TicketDetailsConversations;
